// import _ from 'lodash';
import React from 'react';
import { UseFormMethods } from 'react-hook-form';
import ReactHookFormUtil from 'src/util/ReactHookFormUtil';

interface IRHFInputBoxProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'setValue'>> {
    label?: string;
    labelOnLeft?: boolean;
    labelStyle?: React.CSSProperties;
    title?: string;
    isRequired?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    clearable?: boolean;
    name: string;
    placeholder?: string;
    type?: 'text' | 'date' | 'number' | 'hidden' | 'file' | 'password';
    rules?: unknown;
    onClick?: (event?: React.MouseEvent<HTMLInputElement>) => void;
    onDoubleClick?: (event?: React.MouseEvent<HTMLInputElement>) => void;
    onChange?: (event?: React.FormEvent<HTMLInputElement>) => void;
    onBlur?: (event?: React.FormEvent<HTMLInputElement>) => void;
    onEnterPressed?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
    style?: React.CSSProperties;
    className?: string;
    bodyClassName?: string;
    step?: number;
    max?: number;
    min?: number;
    bodyStyle?: React.CSSProperties;
    defaultVal?: string;
    id?: string;
    spaceLabelContent?: boolean;
}

// const RHFInputBox: React.FC<IRHFInputBoxProps> = props => {
const RHFInputBox = (props: IRHFInputBoxProps) => {
    const handleClear = () => {
        if (props.setValue) {
            props.setValue(props.name, '', { shouldDirty: true, shouldValidate: false });
        }
        props.onChange?.();
    };

    // Mirrors <Select>'s clear "x" - opt-in via `clearable` so existing callers are unaffected.
    // Needs `setValue` (not just `register`) since this is an uncontrolled input; setting the DOM
    // value directly wouldn't update react-hook-form's own tracked state.
    const renderClearButton = () => {
        if (!props.clearable || !props.setValue || props.readOnly || props.disabled) {
            return null;
        }

        return (
            <button
                type="button"
                onClick={handleClear}
                title="Clear"
                style={{
                    position: 'absolute',
                    right: '4px',
                    top: '50%',
                    transform: 'translateY(-50%) translateX(20%)',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#888',
                    fontSize: '1.5rem',
                    lineHeight: 1,
                    padding: '0 4px'
                }}
            >
                X
            </button>
        );
    };

    const wrapWithClearButton = (input: React.ReactElement) => {
        if (!props.clearable) {
            return input;
        }

        return (
            <span style={{ position: 'relative', display: 'inline-block', width: props.style?.width }}>
                {input}
                {renderClearButton()}
            </span>
        );
    };

    // Handles bracketed array paths (e.g. "creditCardDetails[3].chargeAmount") correctly, unlike a
    // naive name.split('.') lookup which would treat "creditCardDetails[3]" as one opaque segment
    // and never match the real nested array errors.formState actually produces for such a field.
    const fieldError = ReactHookFormUtil.getNestedError(props.errors, props.name) as { message?: string; } | undefined;

    if (props.labelOnLeft) {
        return (
            <div className={!!props?.bodyClassName ? props.bodyClassName : 'form-field'} style={props.bodyStyle}>
                {props.label && (
                    props.spaceLabelContent ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <label htmlFor={props.name} style={{ marginRight: '1rem', ...props.labelStyle }}>
                                {props.label}
                                {!!props.isRequired && <span style={{ color: 'red' }}>*</span>}
                            </label>
                            {wrapWithClearButton(
                                <input
                                    id={props?.id !== null && props?.id !== undefined ? props?.id : ''}
                                    name={props.name}
                                    ref={props.register ? props.register(props.rules) : undefined}
                                    type={props.type || 'text'}
                                    className={`field ${props.className ?? ''} ${fieldError ? 'error-field' : ''}`}
                                    max={props.max}
                                    min={props.min}
                                    onClick={props?.onClick}
                                    onDoubleClick={props?.onDoubleClick}
                                    onChange={props?.onChange}
                                    onBlur={props?.onBlur}
                                    onFocus={props?.onFocus}
                                    onPaste={props?.onPaste}
                                    disabled={props.disabled}
                                    style={
                                        !!props.disabled
                                            ? { cursor: 'not-allowed', marginLeft: '1rem', ...props.style }
                                            : (props.clearable ? { paddingRight: '1.25rem', ...props.style } : props.style)
                                    }
                                    step={props.step ? props.step : undefined}
                                    defaultValue={props.defaultVal}
                                    placeholder={props.placeholder}
                                    onKeyDown={(e) => e.key === 'Enter' ? props.onEnterPressed : {}}
                                    title={props.title}
                                    readOnly={props.readOnly}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="form-row">
                            <label htmlFor={props.name} style={{ marginRight: '1rem', ...props.labelStyle }}>
                                {props.label}
                                {!!props.isRequired && <span style={{ color: 'red' }}>*</span>}
                            </label>
                            {wrapWithClearButton(
                                <input
                                    id={props?.id !== null && props?.id !== undefined ? props?.id : ''}
                                    name={props.name}
                                    ref={props.register ? props.register(props.rules) : undefined}
                                    type={props.type || 'text'}
                                    className={`field ${props.className ?? ''} ${fieldError ? 'error-field' : ''}`}
                                    max={props.max}
                                    min={props.min}
                                    onClick={props?.onClick}
                                    onDoubleClick={props?.onDoubleClick}
                                    onChange={props?.onChange}
                                    onBlur={props?.onBlur}
                                    onFocus={props?.onFocus}
                                    onPaste={props?.onPaste}
                                    disabled={props.disabled}
                                    style={
                                        !!props.disabled
                                            ? { cursor: 'not-allowed', marginLeft: '1rem', ...props.style }
                                            : (props.clearable ? { paddingRight: '1.25rem', ...props.style } : props.style)
                                    }
                                    step={props.step ? props.step : undefined}
                                    defaultValue={props.defaultVal}
                                    placeholder={props.placeholder}
                                    onKeyDown={(e) => e.key === 'Enter' ? props.onEnterPressed : {}}
                                    title={props.title}
                                    readOnly={props.readOnly}
                                />
                            )}
                        </div>
                    )
                )}
                {
                    fieldError && (
                        <p style={{ color: 'red', marginLeft: '2px' }}>{fieldError.message}</p>
                    )
                }
            </div >
        );
    } else {
        return (
            <div className={!!props?.bodyClassName ? props.bodyClassName : 'form-field'} style={props.bodyStyle}>
                {props.label && <label htmlFor={props.name} style={props.labelStyle}>
                    {props.label}
                    {!!props.isRequired && <span style={{ color: 'red' }}>*</span>}
                </label>}
                {props.label && props.label && <br />}
                {wrapWithClearButton(
                    <input
                        id={props?.id !== null && props?.id !== undefined ? props?.id : ''}
                        name={props.name}
                        ref={props.register ? props.register(props.rules) : undefined}
                        type={props.type || 'text'}
                        className={`field ${props.className ?? ''} ${fieldError ? 'error-field' : ''}`}
                        max={props.max}
                        min={props.min}
                        onClick={props?.onClick}
                        onDoubleClick={props?.onDoubleClick}
                        onChange={props?.onChange}
                        onBlur={props?.onBlur}
                        onFocus={props?.onFocus}
                        onPaste={props?.onPaste}
                        disabled={props.disabled}
                        style={!!props.disabled ? { cursor: 'not-allowed', ...props.style } : (props.clearable ? { paddingRight: '1.25rem', ...props.style } : props.style)}
                        step={props.step ? props.step : undefined}
                        defaultValue={props.defaultVal}
                        placeholder={props.placeholder}
                        onKeyDown={(e) => e.key === 'Enter' ? props.onEnterPressed : {}}
                        title={props.title}
                        readOnly={props.readOnly}
                    />
                )}
                <br />
                {fieldError && (
                    <p>{fieldError.message}</p>
                )}
            </div>
        );
    }
};

export default RHFInputBox;



// WEBPACK FOOTER //
// ./src/components/controls/tempRHFControls/RHFInputBox.tsx